import styled from 'styled-components6';
import {
    FontAwesomeIcon,
    FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import {
    ChangeEvent,
    FocusEvent,
    KeyboardEvent,
    MouseEvent,
    useEffect,
    useRef,
    useState,
} from 'react';
import { When } from 'react-if';
import { ButtonBase } from '../../theme';

const InputAndButtonsContainer = styled.div`
    display: flex;
    width: 100%;
`;

const INPUT_HEIGHT = 38;

const InputWrapper = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'smallSize' && prop !== 'variant', //remove console warning of invalid prop passed to HTML element
})<{ smallSize: boolean; variant: 'light' | 'dark' }>`
    height: ${INPUT_HEIGHT}px;
    position: relative;
    font-size: 1em;
    line-height: 150%;
    font-weight: normal;
    padding: 10px 14px;
    display: flex;
    gap: var(--rwl-spacing-sm);
    align-items: center;
    align-self: stretch;
    max-width: ${(props) => (props.smallSize ? '35em' : 'unset')};
    border-top-left-radius: var(--rwl-radius-sm);
    border-bottom-left-radius: var(--rwl-radius-sm);
    background: ${(props) =>
        props.variant === 'light'
            ? 'var(--rwl-palette-white)'
            : 'var(--rwl-palette-gray-100)'};

    &.active {
        background: var(--rwl-palette-white);
    }

    &:hover {
        background: ${(props) =>
            props.variant === 'light'
                ? 'var(--rwl-palette-white)'
                : 'var(--rwl-palette-gray-200)'};
    }

    z-index: 20;
    flex: 1;
`;

const Input = styled.input`
    border: none;
    outline: none;
    flex: 1;
    font-size: 1em;
    line-height: 150%;
    font-weight: normal;
    padding: 0;
    background: transparent;
    min-width: 0;
`;

const AutocompleteWrapper = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 4px;
    border: 1px solid var(--rwl-palette-gray-200);
    background: var(--rwl-palette-white, #fff);
`;

const AutocompleteItem = styled.div`
    display: flex;
    padding: 10px 14px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    cursor: pointer;
    border-top: 1px solid var(--rwl-palette-gray-200);

    &:first-child {
        border-top: none;
    }

    &:focus,
    &.active {
        background: var(--rwl-palette-gray-100);
    }
`;

const AutocompleteButtonBase = styled(ButtonBase).withConfig({
    shouldForwardProp: (prop) => prop !== 'parentIsHovered', // remove console warning of invalid prop passed to HTML element
})<{ parentIsHovered: boolean; variant: 'light' | 'dark' }>`
    align-items: center;
    background: ${({ parentIsHovered, variant }) =>
        variant === 'dark'
            ? `var(--rwl-palette-gray-${parentIsHovered ? '200' : '100'})`
            : 'var(--rwl-palette-white)'};
    height: ${INPUT_HEIGHT}px;
    &:hover {
        background: ${({ variant }) =>
            variant === 'dark'
                ? `var(--rwl-palette-gray-200)`
                : 'var(--rwl-palette-white)'};
    }

    // icons
    svg {
        color: var(--rwl-palette-gray-500);
    }

    // buttons
    a {
        padding: var(--rwl-spacing-sm);
    }
`;

export enum AutocompleteTypes {
    CURRENT = 'current',
    RECENT = 'recent',
    SUGGESTION = 'suggestion',
}

export type AutocompleteOption = {
    type: AutocompleteTypes;
    icon?: FontAwesomeIconProps['icon'];
    key: string;
    label: string;
    onSelected?: () => void;
};

type TypeAheadInputProps = {
    suggestions: AutocompleteOption[];
    prefilledValue?: string;
    onTyping?: (value: string) => void;
    onClear?: () => void;
    smallSize?: boolean;
    variant?: 'light' | 'dark';
    onFocus?: () => void;
    onBlur?: () => void;
};

export const TypeAheadInput = ({
    suggestions,
    prefilledValue,
    onTyping = () => void 0,
    onClear = () => void 0,
    smallSize = true,
    variant = 'dark',
    onFocus = () => void 0,
    onBlur = () => void 0,
}: TypeAheadInputProps) => {
    const inputEl = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState('');
    const [tempValue, setTempValue] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedOption, setHighlightedOption] =
        useState<AutocompleteOption | null>(null);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        setValue(prefilledValue ?? '');
        setTempValue(prefilledValue ?? '');
    }, [prefilledValue]);

    useEffect(() => {
        if (isInputFocused) {
            setIsOpen(true);
        }
    }, [isInputFocused]);

    useEffect(() => {
        if (value !== tempValue) {
            setTempValue(value);
        }
    }, [value]);

    useEffect(() => {
        onTyping(tempValue);
    }, [tempValue]);

    useEffect(() => {
        if (!isOpen) {
            setHighlightedOption(null);
            onBlur();
        } else {
            inputEl.current?.focus();
            onFocus();
        }
    }, [isOpen]);

    const onSelectOption = (option: AutocompleteOption) => {
        if (option) {
            setValue(option.label);
            if (option.onSelected) {
                option.onSelected();
            }
        }
        setIsOpen(false);
        setIsHovered(false);
    };

    return (
        <InputAndButtonsContainer>
            <InputWrapper
                variant={variant}
                smallSize={smallSize}
                onMouseOver={() => setIsHovered(true)}
                onMouseOut={() => setIsHovered(false)}
                onMouseDown={() => setIsOpen(true)}
                onFocus={() => setIsOpen(true)}
                onBlur={() => setIsOpen(false)}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                    if (
                        highlightedOption &&
                        (e.key === 'Enter' || e.key === 'Tab')
                    ) {
                        onSelectOption(highlightedOption);
                        return;
                    }
                    if (
                        !highlightedOption &&
                        (e.key === 'Enter' || e.key === 'Tab')
                    ) {
                        onSelectOption(
                            suggestions.find((s) => s.type === 'suggestion')!
                        );
                        return;
                    }
                    if (e.key === 'Escape') {
                        setTempValue(value);
                        inputEl.current?.blur();
                        setIsOpen(false);
                        return;
                    }
                    if (e.key === 'ArrowDown') {
                        const index = suggestions.findIndex((option) =>
                            areOptionsTheSame(option, highlightedOption)
                        );
                        if (index === -1) {
                            setHighlightedOption(suggestions[0]);
                        } else {
                            setHighlightedOption(
                                suggestions[index + 1] ?? null
                            );
                        }
                        return;
                    }
                    if (e.key === 'ArrowUp') {
                        const index = suggestions.findIndex((option) =>
                            areOptionsTheSame(option, highlightedOption)
                        );
                        if (index === -1) {
                            setHighlightedOption(
                                suggestions[suggestions.length - 1]
                            );
                        } else {
                            setHighlightedOption(
                                suggestions[index - 1] ?? null
                            );
                        }
                        return;
                    }
                }}
                className={isOpen ? 'active' : ''}
            >
                <Input
                    ref={inputEl}
                    placeholder={'Address, neighborhood, or zip code'}
                    value={tempValue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setTempValue(e.target.value)
                    }
                    onFocus={(e: FocusEvent<HTMLInputElement>) => {
                        e.target.select();
                        setIsInputFocused(true);
                    }}
                    onBlur={() => setIsInputFocused(false)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (
                            isOpen &&
                            (e.key === 'ArrowDown' || e.key === 'ArrowUp')
                        ) {
                            e.preventDefault();
                        }
                    }}
                />
                <When condition={isOpen}>
                    <AutocompleteWrapper>
                        {suggestions.map((option) => {
                            const { type, key, label, icon } = option;
                            const isActive = areOptionsTheSame(
                                option,
                                highlightedOption
                            );
                            return (
                                <AutocompleteItem
                                    key={`${type}-${key}`}
                                    onMouseDown={(
                                        e: MouseEvent<HTMLDivElement>
                                    ) => {
                                        e.stopPropagation();
                                        onSelectOption(option);
                                    }}
                                    className={isActive ? 'active' : ''}
                                    onMouseMove={() =>
                                        setHighlightedOption(option)
                                    }
                                >
                                    <When condition={!!icon}>
                                        <FontAwesomeIcon
                                            icon={icon!}
                                            fontSize={'20px'}
                                        />
                                    </When>
                                    {label}
                                </AutocompleteItem>
                            );
                        })}
                    </AutocompleteWrapper>
                </When>
            </InputWrapper>
            <When condition={value?.length || tempValue?.length}>
                <AutocompleteButtonBase
                    parentIsHovered={isHovered}
                    onClick={() => {
                        setValue('');
                        setTempValue('');
                        onClear();
                    }}
                    variant={variant}
                >
                    <FontAwesomeIcon icon={faXmark} fontSize={'20px'} />
                </AutocompleteButtonBase>
            </When>
            <AutocompleteButtonBase
                parentIsHovered={isHovered}
                onClick={() => {
                    if (highlightedOption) {
                        onSelectOption(highlightedOption);
                        return;
                    } else {
                        onSelectOption(
                            suggestions.find((s) => s.type === 'suggestion')!
                        );
                        return;
                    }
                }}
                style={{
                    borderBottomRightRadius: 'var(--rwl-radius-sm)',
                    borderTopRightRadius: 'var(--rwl-radius-sm)',
                }}
                variant={variant}
            >
                <FontAwesomeIcon
                    icon={faSearch}
                    fontSize={'20px'}
                    style={{ cursor: 'pointer' }}
                />
            </AutocompleteButtonBase>
        </InputAndButtonsContainer>
    );
};

function areOptionsTheSame(
    option1: AutocompleteOption | null,
    option2: AutocompleteOption | null
) {
    return option1?.key === option2?.key && option1?.type === option2?.type;
}
